import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Lock, Truck, Shield, Check, Tag, ChevronDown, ChevronUp, X, Ticket, Plus, MapPin, Star, Trash2, Pencil, Coins } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { orders, coupons as couponsApi, addresses as addressesApi, rewards, type CouponAPI, type AddressAPI } from '../api/client';
import { getAttribution } from '../hooks/useAttribution';
import { getStateNames } from '../data/indiaLocations';
import SEO from '../components/SEO';
import { pageSeoMeta } from '@shared/pageSeoMeta';
import { freeShippingState, shippingFee } from '../lib/shipping';
import PhoneInput, { maxDigitsFor } from '../components/PhoneInput';
import { parseStoredPhone, type Country } from '../data/countryCodes';
import OrderCelebration from '../components/OrderCelebration';
import { cashbackRate, getBadgeForOrders } from '../lib/badges';

export default function CheckoutPage() {
  const { state, totalPrice, clearCart, appliedCoupon, discountAmount, setAppliedCoupon, removeCoupon: clearCoupon } = useCart();
  const { isDark } = useTheme();
  const { user, isLoading: authLoading, refreshUser } = useAuth();
  const location = useLocation();
  const [step, setStep] = useState<'info' | 'submitting' | 'success'>('info');
  const [orderError, setOrderError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const initialPhone = parseStoredPhone(user?.phone);
  const [form, setForm] = useState({
    firstName: user?.first_name ?? '',
    lastName: user?.last_name ?? '',
    email: user?.email ?? '',
    phone: initialPhone.nationalNumber,
    address: '',
    city: '',
    state: '',
    pincode: '',
  });
  const [country, setCountry] = useState<Country>(initialPhone.country);

  const shipping = shippingFee(totalPrice);
  const freeShip = freeShippingState(totalPrice);
  const stateNames = useMemo(() => getStateNames(), []);

  // ── Saved address state ──
  const [savedAddresses, setSavedAddresses] = useState<AddressAPI[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [addressMode, setAddressMode] = useState<'loading' | 'selected' | 'pick' | 'form'>('loading');
  const [editingAddressId, setEditingAddressId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [addressError, setAddressError] = useState<string | null>(null);

  const [addressFormData, setAddressFormData] = useState({ address_line: '', city: '', state: '', pincode: '', is_default: false });

  const validateAddressData = (data: { address_line: string; city: string; state: string; pincode: string }) => {
    if (!data.address_line.trim()) return 'Address line is required.';
    if (!data.state) return 'Please select a state.';
    if (!data.city.trim()) return 'City is required.';
    if (!/^\d{6}$/.test(data.pincode)) return 'PIN code must be exactly 6 digits.';
    return null;
  };

  // Load saved addresses on mount
  useEffect(() => {
    if (!user) return;
    addressesApi.list().then((list) => {
      setSavedAddresses(list);
      if (list.length > 0) {
        const def = list.find(a => a.is_default) ?? list[0];
        selectAddress(def);
        setAddressMode('selected');
      } else {
        setAddressMode('form');
      }
    }).catch(() => {
      setAddressMode('form');
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const selectAddress = (addr: AddressAPI) => {
    setSelectedAddressId(addr.id);
    setForm(f => ({ ...f, address: addr.address_line, city: addr.city, state: addr.state, pincode: addr.pincode }));
  };

  const handleSaveNewAddress = async (overrideData?: typeof addressFormData) => {
    const data = overrideData ?? addressFormData;
    const err = validateAddressData(data);
    if (err) { setAddressError(err); return; }
    setAddressError(null);
    try {
      const created = await addressesApi.create(data);
      setSavedAddresses(prev => {
        const updated = created.is_default ? prev.map(a => ({ ...a, is_default: false })) : prev;
        return [created, ...updated];
      });
      selectAddress(created);
      setAddressMode('selected');
      setAddressFormData({ address_line: '', city: '', state: '', pincode: '', is_default: false });
    } catch {
      setOrderError('Failed to save address');
    }
  };

  const handleUpdateAddress = async () => {
    if (!editingAddressId) return;
    const err = validateAddressData(addressFormData);
    if (err) { setAddressError(err); return; }
    setAddressError(null);
    try {
      const updated = await addressesApi.update(editingAddressId, addressFormData);
      setSavedAddresses(prev => prev.map(a => a.id === updated.id ? updated : (updated.is_default ? { ...a, is_default: false } : a)));
      selectAddress(updated);
      setEditingAddressId(null);
      setAddressMode('selected');
      setAddressFormData({ address_line: '', city: '', state: '', pincode: '', is_default: false });
    } catch {
      setOrderError('Failed to update address');
    }
  };

  const handleDeleteAddress = async (id: string) => {
    setConfirmDeleteId(null);
    try {
      await addressesApi.remove(id);
      const remaining = savedAddresses.filter(a => a.id !== id);
      setSavedAddresses(remaining);
      if (selectedAddressId === id) {
        if (remaining.length > 0) {
          const next = remaining.find(a => a.is_default) ?? remaining[0];
          selectAddress(next);
          setAddressMode('selected');
        } else {
          setSelectedAddressId(null);
          setForm(f => ({ ...f, address: '', city: '', state: '', pincode: '' }));
          setAddressMode('form');
        }
      }
    } catch {
      setOrderError('Failed to delete address');
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await addressesApi.setDefault(id);
      setSavedAddresses(prev => prev.map(a => ({ ...a, is_default: a.id === id })));
    } catch {
      setOrderError('Failed to set default');
    }
  };

  // Coupon state (appliedCoupon + discountAmount live in CartContext, persisted from cart)
  const [availableCoupons, setAvailableCoupons] = useState<CouponAPI[]>([]);
  const [couponInput, setCouponInput] = useState('');
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [showCoupons, setShowCoupons] = useState(false);

  // ZS Coins redemption — coin value comes from the API (server is the source of truth).
  const [coinValue, setCoinValue] = useState(1);
  const [coinBalance, setCoinBalance] = useState(0);
  const [coinsToRedeem, setCoinsToRedeem] = useState(0);
  const [coinError, setCoinError] = useState(false); // coins balance couldn't be loaded

  // Order-placed celebration
  const [earnedCoins, setEarnedCoins] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);

  useEffect(() => {
    couponsApi.active().then(setAvailableCoupons).catch(() => {});
    rewards.coins()
      .then((r) => { setCoinBalance(r.balance); setCoinValue(r.coin_value_inr || 1); setCoinError(false); })
      .catch(() => { setCoinBalance(0); setCoinError(true); });
  }, []);

  // Re-validate applied coupon on checkout load (safety net — CartPage handles cart changes,
  // this catches any stale sessionStorage discount when arriving directly at checkout)
  useEffect(() => {
    if (!appliedCoupon || totalPrice === 0) return;
    couponsApi.validate(appliedCoupon.code, totalPrice).then((res) => {
      if (res.valid) {
        setAppliedCoupon(appliedCoupon, res.discount_amount);
      } else {
        clearCoupon();
        setCouponError(res.message);
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPrice]);

  // Scroll to error message whenever it is set
  useEffect(() => {
    if (orderError && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [orderError]);

  const applyCoupon = async (code: string) => {
    setCouponError(null);
    setCouponLoading(true);
    try {
      const res = await couponsApi.validate(code, totalPrice);
      if (res.valid) {
        setAppliedCoupon(res.coupon, res.discount_amount);
        setCouponInput('');
        setShowCoupons(false);
      } else {
        setCouponError(res.message);
      }
    } catch {
      setCouponError('Failed to validate coupon');
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    clearCoupon();
    setCouponError(null);
  };

  // Coins: cap by balance, 20% of subtotal, and remaining payable after coupon.
  const afterCoupon = totalPrice + shipping - discountAmount;
  const maxCoins = Math.max(0, Math.min(
    coinBalance,
    Math.floor((totalPrice * 0.2) / coinValue),
    Math.floor(afterCoupon / coinValue),
  ));
  const effectiveCoins = Math.min(coinsToRedeem, maxCoins);
  const coinDiscount = Math.floor(effectiveCoins * coinValue);
  const grandTotal = totalPrice + shipping - discountAmount - coinDiscount;

  // Redirect to login if not authenticated
  if (!authLoading && !user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const showError = (msg: string) => {
      setOrderError(msg);
      setTimeout(() => errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0);
    };

    if (editingAddressId) {
      showError('Please save or cancel your address changes before placing the order.');
      return;
    }

    if (addressMode === 'form') {
      const addrErr = validateAddressData({ address_line: form.address, city: form.city, state: form.state, pincode: form.pincode });
      if (addrErr) { showError(addrErr); return; }
    }

    if (!form.firstName.trim()) { showError('Please enter your first name.'); return; }
    if (!form.lastName.trim()) { showError('Please enter your last name.'); return; }
    if (!form.phone.trim()) { showError('Please enter your phone number.'); return; }
    const cleanPhone = form.phone.replace(/\D/g, '').slice(0, maxDigitsFor(country));
    if (country.iso === 'in') {
      // India: exactly 10 national digits.
      if (cleanPhone.length !== 10) { showError('Please enter a valid 10-digit phone number.'); return; }
    } else if (!/^\+?\d{7,15}$/.test(`${country.dialCode}${cleanPhone}`)) {
      // Others: mirror the server rule (full number, incl. country code, is 7–15 digits)
      // so a short/long number is caught here with a clear message instead of a server error.
      showError('Please enter a valid phone number.'); return;
    }

    setOrderError(null);
    setStep('submitting');
    try {
      // Auto-save address for first-time users (no saved addresses yet)
      if (addressMode === 'form' && savedAddresses.length === 0 && form.address && form.city && form.state && form.pincode) {
        try {
          const created = await addressesApi.create({
            address_line: form.address,
            city: form.city,
            state: form.state,
            pincode: form.pincode,
            is_default: true,
          });
          setSavedAddresses(prev => [created, ...prev]);
        } catch { /* non-blocking */ }
      }
      const placed = await orders.place({
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        phone: form.phone ? `${country.dialCode}${cleanPhone}` : '',
        address: form.address,
        city: form.city,
        state: form.state,
        pincode: form.pincode,
        items: state.items.map(i => ({ product_id: i.product.id, quantity: i.quantity })),
        ...(appliedCoupon ? { coupon_code: appliedCoupon.code } : {}),
        ...(effectiveCoins > 0 ? { coins_to_redeem: effectiveCoins } : {}),
        ...getAttribution(),
      });
      // Projected cashback: this order will be the customer's next delivered one,
      // so rank it at (current delivered count + 1). Earn is on the net total.
      const rate = cashbackRate((user?.order_count ?? 0) + 1);
      setEarnedCoins(Math.floor((placed.total ?? 0) * rate));
      setShowCelebration(true);
      setStep('success');
      clearCart();
      // Coins may have been redeemed on this order — refresh the cached user so
      // the navbar ZS Coins pill reflects the new balance. Best-effort: the
      // order already succeeded, so a refresh failure must NOT surface as an
      // order error (hence the swallowed catch here specifically).
      refreshUser().catch(() => {});
    } catch (err) {
      setOrderError(err instanceof Error ? err.message : 'Failed to place order');
      setStep('info');
    }
  };

  if (state.items.length === 0 && step !== 'success') {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
        <div className="text-center">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Nothing to Checkout</h2>
          <Link to="/store" className="text-emerald-600 font-medium hover:underline">
            Browse Store
          </Link>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className={`text-center max-w-md rounded-3xl p-12 shadow-sm border ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}
        >
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <Check size={40} className="text-emerald-600" />
          </div>
          <h1 className={`text-2xl font-extrabold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Order Placed!</h1>
          <p className={`mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Thank you for your order. We'll send you a confirmation email shortly.
          </p>
          <p className={`text-sm mb-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Your order will be shipped within 24 hours.
          </p>
          <Link
            to="/store"
            className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-all"
          >
            Continue Shopping
          </Link>
        </motion.div>

        <AnimatePresence>
          {showCelebration && (
            <OrderCelebration
              earnedCoins={earnedCoins}
              tier={getBadgeForOrders(user?.order_count ?? 0)}
              onClose={() => setShowCelebration(false)}
            />
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
      <SEO {...pageSeoMeta.checkout} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/cart" className={`inline-flex items-center gap-2 mb-8 ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>
          <ArrowLeft size={16} /> Back to Cart
        </Link>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Form */}
          <form id="checkout-form" onSubmit={handleSubmit} noValidate className="lg:col-span-3 space-y-6">
            {/* Error banner — always at top so it's visible immediately */}
            {orderError && (
              <div ref={errorRef} className="rounded-xl p-4 bg-red-500/10 border border-red-500/20 text-red-400 text-sm text-center">
                {orderError}
              </div>
            )}
            {/* Personal info */}
            <div className={`rounded-2xl border p-6 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
              <h2 className={`text-lg font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>Personal Information</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { name: 'firstName', label: 'First Name', type: 'text', ph: 'John' },
                  { name: 'lastName', label: 'Last Name', type: 'text', ph: 'Doe' },
                ].map((field) => (
                  <div key={field.name}>
                    <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{field.label}</label>
                    <input
                      type={field.type}
                      placeholder={field.ph}
                      required
                      value={form[field.name as keyof typeof form]}
                      onChange={(e) => setForm({ ...form, [field.name]: e.target.value })}
                      className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm ${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'border-gray-200'}`}
                    />
                  </div>
                ))}
                <div className="sm:col-span-2">
                  <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Email</label>
                  <input type="email" required readOnly value={form.email} className={`w-full px-4 py-3 rounded-xl border focus:outline-none text-sm opacity-60 cursor-not-allowed ${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'border-gray-200'}`} />
                </div>
                <div className="sm:col-span-2">
                  <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Phone</label>
                  <PhoneInput
                    country={country}
                    onCountryChange={setCountry}
                    value={form.phone}
                    onChange={(v) => setForm({ ...form, phone: v })}
                    isDark={isDark}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Shipping address */}
            <div className={`rounded-2xl border p-6 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className={`text-lg font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <MapPin size={18} /> Shipping Address
                </h2>
                {addressMode === 'selected' && (
                  <button
                    type="button"
                    onClick={() => setAddressMode('pick')}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${isDark ? 'bg-gray-800 text-emerald-400 hover:text-emerald-300' : 'bg-emerald-50 text-emerald-600 hover:text-emerald-700'}`}
                  >
                    Change
                  </button>
                )}
                {(addressMode === 'pick' || addressMode === 'form') && savedAddresses.length > 0 && (
                  <button
                    type="button"
                    onClick={() => { setAddressMode('selected'); setEditingAddressId(null); }}
                    className={`text-xs font-medium ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    Cancel
                  </button>
                )}
              </div>

              {/* Loading */}
              {addressMode === 'loading' && (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
                </div>
              )}

              {/* Selected address card */}
              {addressMode === 'selected' && selectedAddressId && (() => {
                const addr = savedAddresses.find(a => a.id === selectedAddressId);
                if (!addr) return null;
                return (
                  <div className={`rounded-xl border-2 border-emerald-500 p-4 ${isDark ? 'bg-emerald-500/5' : 'bg-emerald-50/50'}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check size={14} className="text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{addr.address_line}</p>
                        <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {addr.city}, {addr.state} — {addr.pincode}
                        </p>
                        {addr.is_default && (
                          <span className="inline-flex items-center gap-1 mt-2 text-[10px] font-semibold uppercase tracking-wider text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">
                            <Star size={10} /> Default
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Pick from saved addresses */}
              {addressMode === 'pick' && (
                <div className="space-y-3">
                  {savedAddresses.map((addr) => (
                    <div
                      key={addr.id}
                      className={`rounded-xl border p-4 cursor-pointer transition-all ${
                        selectedAddressId === addr.id
                          ? `border-emerald-500 ${isDark ? 'bg-emerald-500/5' : 'bg-emerald-50/50'}`
                          : isDark ? 'border-gray-700 hover:border-gray-600 bg-gray-800/50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => { selectAddress(addr); setAddressMode('selected'); }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            selectedAddressId === addr.id ? 'border-emerald-500 bg-emerald-500' : isDark ? 'border-gray-600' : 'border-gray-300'
                          }`}>
                            {selectedAddressId === addr.id && <Check size={10} className="text-white" />}
                          </div>
                          <div>
                            <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{addr.address_line}</p>
                            <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {addr.city}, {addr.state} — {addr.pincode}
                            </p>
                            {addr.is_default && (
                              <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">
                                <Star size={10} /> Default
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                          {!addr.is_default && (
                            <button
                              type="button"
                              onClick={() => handleSetDefault(addr.id)}
                              title="Set as default"
                              className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-500 hover:text-yellow-400' : 'hover:bg-gray-100 text-gray-400 hover:text-yellow-500'}`}
                            >
                              <Star size={14} />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setEditingAddressId(addr.id);
                              setAddressFormData({ address_line: addr.address_line, city: addr.city, state: addr.state, pincode: addr.pincode, is_default: addr.is_default });
                              setAddressMode('form');
                            }}
                            title="Edit"
                            className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-500 hover:text-blue-400' : 'hover:bg-gray-100 text-gray-400 hover:text-blue-500'}`}
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteId(addr.id)}
                            title="Delete"
                            className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-500 hover:text-red-400' : 'hover:bg-gray-100 text-gray-400 hover:text-red-500'}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Add new address button */}
                  <button
                    type="button"
                    onClick={() => {
                      setEditingAddressId(null);
                      setAddressFormData({ address_line: '', city: '', state: '', pincode: '', is_default: false });
                      setForm(f => ({ ...f, address: '', city: '', state: '', pincode: '' }));
                      setAddressMode('form');
                    }}
                    className={`w-full rounded-xl border-2 border-dashed p-4 flex items-center justify-center gap-2 text-sm font-medium transition-colors ${
                      isDark ? 'border-gray-700 text-gray-400 hover:border-emerald-500 hover:text-emerald-400' : 'border-gray-200 text-gray-500 hover:border-emerald-500 hover:text-emerald-600'
                    }`}
                  >
                    <Plus size={16} /> Add New Address
                  </button>
                </div>
              )}

              {/* Address form (new or edit) */}
              {addressMode === 'form' && (
                <div className="space-y-4">
                  <p className={`text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {editingAddressId ? 'Edit Address' : 'Add New Address'}
                  </p>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Address Line</label>
                    <input
                      type="text"
                      placeholder="123 Main Street, Apartment 4B"
                      required
                      value={editingAddressId ? addressFormData.address_line : form.address}
                      onChange={(e) => {
                        if (editingAddressId) setAddressFormData({ ...addressFormData, address_line: e.target.value });
                        else setForm({ ...form, address: e.target.value });
                      }}
                      className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm ${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'border-gray-200'}`}
                    />
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>State</label>
                      <div className="relative">
                        <select
                          required
                          value={editingAddressId ? addressFormData.state : form.state}
                          onChange={(e) => {
                            if (editingAddressId) setAddressFormData({ ...addressFormData, state: e.target.value });
                            else setForm({ ...form, state: e.target.value });
                          }}
                          className={`w-full px-4 py-3 pr-10 rounded-xl border appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'border-gray-200 bg-white text-gray-900'}`}
                        >
                          <option value="">Select State</option>
                          {stateNames.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                        <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                      </div>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>City</label>
                      <input
                        type="text"
                        placeholder="Mumbai"
                        required
                        value={editingAddressId ? addressFormData.city : form.city}
                        onChange={(e) => {
                          if (editingAddressId) setAddressFormData({ ...addressFormData, city: e.target.value });
                          else setForm({ ...form, city: e.target.value });
                        }}
                        className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm ${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'border-gray-200'}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>PIN Code</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="400001"
                        required
                        maxLength={6}
                        pattern="[0-9]{6}"
                        value={editingAddressId ? addressFormData.pincode : form.pincode}
                        onChange={(e) => {
                          const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                          if (editingAddressId) setAddressFormData({ ...addressFormData, pincode: v });
                          else setForm({ ...form, pincode: v });
                          if (addressError) setAddressError(null);
                        }}
                        className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm ${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'border-gray-200'}`}
                      />
                    </div>
                  </div>

                  {addressError && (
                    <p className="text-xs text-red-400 font-medium">{addressError}</p>
                  )}

                  {/* Save / Update buttons */}
                  {editingAddressId ? (
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={addressFormData.is_default}
                          onChange={(e) => setAddressFormData({ ...addressFormData, is_default: e.target.checked })}
                          className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Set as default</span>
                      </label>
                      <div className="flex-1" />
                      <button
                        type="button"
                        onClick={() => { setEditingAddressId(null); setAddressMode(savedAddresses.length > 0 ? 'pick' : 'selected'); }}
                        className={`px-4 py-2 text-sm font-medium rounded-lg ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleUpdateAddress}
                        className="px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
                      >
                        Update Address
                      </button>
                    </div>
                  ) : savedAddresses.length > 0 ? (
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={addressFormData.is_default}
                          onChange={(e) => setAddressFormData({ ...addressFormData, is_default: e.target.checked })}
                          className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Set as default</span>
                      </label>
                      <div className="flex-1" />
                      <button
                        type="button"
                        onClick={() => handleSaveNewAddress({ address_line: form.address, city: form.city, state: form.state, pincode: form.pincode, is_default: addressFormData.is_default })}
                        className="px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
                      >
                        Save & Use Address
                      </button>
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            <div className={`rounded-2xl border p-6 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
              <h2 className={`text-lg font-bold mb-6 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <Truck size={18} /> Payment Method
              </h2>
              <div className={`rounded-xl p-4 flex items-center gap-3 border-2 border-emerald-500 ${isDark ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}>
                <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                  <Check size={18} className="text-white" />
                </div>
                <div>
                  <p className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>Cash on Delivery (COD)</p>
                  <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Pay when your order is delivered to your doorstep</p>
                </div>
              </div>
            </div>

            {/* Desktop-only — on mobile the button appears below the order summary */}
            <div className="hidden lg:block space-y-4">
              <button
                type="submit"
                disabled={step === 'submitting'}
                className="w-full py-4 bg-emerald-600 text-white rounded-xl font-bold text-lg hover:bg-emerald-700 transition-all hover:shadow-lg hover:shadow-emerald-600/25 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {step === 'submitting' ? (
                  <><div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" /> Processing...</>
                ) : (
                  <><Lock size={18} /> Place Order — ₹{grandTotal.toLocaleString('en-IN')}</>
                )}
              </button>

              <div className={`flex items-center justify-center gap-6 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                <span className="flex items-center gap-1"><Lock size={12} /> Encrypted</span>
                <span className="flex items-center gap-1"><Shield size={12} /> Secure</span>
                <span className="flex items-center gap-1"><Truck size={12} /> Fast Shipping</span>
              </div>
            </div>
          </form>

          {/* Order summary */}
          <div className="lg:col-span-2">
            <div className={`rounded-2xl border p-6 sticky top-24 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
              <h3 className={`text-lg font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>Order Summary</h3>
              <div className="space-y-3 mb-6">
                {state.items.map((item) => (
                  <div key={item.product.id} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden relative"
                        style={{ backgroundColor: item.product.colorAccent + '10' }}
                      >
                        <img
                          src={item.product.image}
                          alt={`${item.product.name} nicotine pouches`}
                          className="w-8 h-8 object-contain"
                          loading="lazy"
                        />
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                          {item.quantity}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className={`truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{item.product.name}</p>
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{item.quantity} × ₹{item.product.price.toLocaleString('en-IN')}</p>
                      </div>
                    </div>
                    <span className={`font-medium flex-shrink-0 ml-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      ₹{(item.product.price * item.quantity).toLocaleString('en-IN')}
                    </span>
                  </div>
                ))}
              </div>
              <div className={`border-t pt-4 space-y-2 ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                <div className="flex justify-between text-sm">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Subtotal</span>
                  <span className="font-medium">{'\u20b9'}{totalPrice.toLocaleString('en-IN')}</span>
                </div>

                {/* Coupon Section */}
                {appliedCoupon ? (
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-1.5">
                      <Tag size={14} className="text-emerald-500" />
                      <span className="text-emerald-500 font-medium">{appliedCoupon.code}</span>
                      <button onClick={removeCoupon} className="text-gray-400 hover:text-red-400 transition-colors">
                        <X size={14} />
                      </button>
                    </div>
                    <span className="text-emerald-500 font-medium">-{'\u20b9'}{discountAmount.toLocaleString('en-IN')}</span>
                  </div>
                ) : (
                  <div>
                    <button
                      onClick={() => setShowCoupons(!showCoupons)}
                      className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${isDark ? 'text-emerald-400 hover:text-emerald-300' : 'text-emerald-600 hover:text-emerald-700'}`}
                    >
                      <Ticket size={14} />
                      Apply Coupon
                      {showCoupons ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>

                    <AnimatePresence>
                      {showCoupons && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="pt-3 space-y-3">
                            {/* Manual input */}
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={couponInput}
                                onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCouponError(null); }}
                                placeholder="Enter code"
                                className={`flex-1 px-3 py-2 rounded-lg border text-sm font-mono uppercase ${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'border-gray-200'} focus:outline-none focus:ring-2 focus:ring-emerald-500`}
                              />
                              <button
                                type="button"
                                onClick={() => couponInput && applyCoupon(couponInput)}
                                disabled={!couponInput || couponLoading}
                                className="px-3 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                              >
                                {couponLoading ? '...' : 'Apply'}
                              </button>
                            </div>

                            {couponError && (
                              <p className="text-xs text-red-400">{couponError}</p>
                            )}

                            {/* Available coupons list */}
                            {availableCoupons.length > 0 && (
                              <div className="space-y-2">
                                <p className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Available Coupons</p>
                                {availableCoupons.map((c) => {
                                  const eligible = !c.min_order_value || totalPrice >= c.min_order_value;
                                  const needed = c.min_order_value ? c.min_order_value - totalPrice : 0;
                                  return (
                                    <div
                                      key={c.id}
                                      className={`rounded-lg border p-3 ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-100 bg-gray-50'}`}
                                    >
                                      <div className="flex items-center justify-between mb-1">
                                        <code className={`text-xs font-bold tracking-wider ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{c.code}</code>
                                        {eligible ? (
                                          <button
                                            type="button"
                                            onClick={() => applyCoupon(c.code)}
                                            disabled={couponLoading}
                                            className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 transition-colors disabled:opacity-50"
                                          >
                                            APPLY
                                          </button>
                                        ) : (
                                          <span className="text-xs text-amber-500 font-medium">Add {'\u20b9'}{needed.toLocaleString('en-IN')} more</span>
                                        )}
                                      </div>
                                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {c.discount_type === 'percentage'
                                          ? `${c.discount_value}% off${c.max_discount ? ` (max \u20b9${c.max_discount.toLocaleString('en-IN')})` : ''}`
                                          : `\u20b9${c.discount_value.toLocaleString('en-IN')} off`}
                                        {c.min_order_value ? ` on orders above \u20b9${c.min_order_value.toLocaleString('en-IN')}` : ''}
                                      </p>
                                      {c.valid_until && (
                                        <p className={`text-[10px] mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                          Expires {new Date(c.valid_until).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                        </p>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {/* ZS Coins */}
                {coinError && (
                  <p className="text-[11px] text-amber-500 flex items-center gap-1.5">
                    <Coins size={12} /> Couldn't load your ZS Coins balance — refresh the page to redeem coins.
                  </p>
                )}
                {coinBalance > 0 && (
                  <div className={`rounded-lg border p-3 ${isDark ? 'border-gray-700 bg-gray-800/40' : 'border-gray-100 bg-gray-50'}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-medium flex items-center gap-1.5"><Coins size={14} className="text-emerald-500" /> ZS Coins</span>
                      <span className={`text-xs inline-flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Balance:
                        <span className="leading-none" aria-hidden="true">🪙</span>
                        <span className="font-bold text-amber-500">{coinBalance.toLocaleString('en-IN')}</span>
                      </span>
                    </div>
                    {maxCoins > 0 ? (
                      <>
                        <div className="flex items-center gap-2">
                          <input type="range" min={0} max={maxCoins} value={effectiveCoins}
                            onChange={(e) => setCoinsToRedeem(Number(e.target.value))}
                            className="flex-1 accent-emerald-500" />
                          <button type="button" onClick={() => setCoinsToRedeem(maxCoins)}
                            className="text-xs font-semibold text-emerald-600 hover:text-emerald-700">MAX</button>
                        </div>
                        <p className={`text-[11px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          Using {effectiveCoins} coins {'→'} {'−₹'}{coinDiscount.toLocaleString('en-IN')} <span className="opacity-70">(up to 20% of subtotal)</span>
                        </p>
                      </>
                    ) : (
                      <p className={`text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Nothing to redeem on this order.</p>
                    )}
                  </div>
                )}
                {coinDiscount > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1.5 text-emerald-500 font-medium"><Coins size={14} /> Coins</span>
                    <span className="text-emerald-500 font-medium">{'−₹'}{coinDiscount.toLocaleString('en-IN')}</span>
                  </div>
                )}

                <div className="flex justify-between text-sm">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Shipping</span>
                  <span className={shipping === 0 ? 'text-emerald-600 font-medium' : 'font-medium'}>
                    {shipping === 0 ? 'FREE' : `\u20b9${shipping}`}
                  </span>
                </div>
                {freeShip.reached ? (
                  <p className="text-[11px] text-emerald-600 flex items-center gap-1 -mt-1">
                    <Check size={11} /> Free shipping applied
                  </p>
                ) : (
                  <div className="-mt-1">
                    <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                      <div
                        className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all duration-500 ease-out"
                        style={{ width: `${freeShip.pct}%` }}
                      />
                    </div>
                    <p className={`text-[11px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {'\u20b9'}{freeShip.remaining.toLocaleString('en-IN')} away from free shipping &middot;{' '}
                      <Link to="/cart" className="text-emerald-600 hover:underline">add more</Link>
                    </p>
                  </div>
                )}
                <div className={`border-t pt-3 flex justify-between ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                  <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Total</span>
                  <span className={`text-xl font-extrabold ${isDark ? 'text-white' : 'text-gray-900'}`}>{`\u20b9`}{grandTotal.toLocaleString('en-IN')}</span>
                </div>
                {discountAmount > 0 && (
                  <p className="text-xs text-emerald-500 text-right font-medium">You save {'\u20b9'}{discountAmount.toLocaleString('en-IN')} with coupon!</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile: Place Order button below order summary */}
        <div className="lg:hidden mt-6 space-y-4">
          <button
            type="submit"
            form="checkout-form"
            disabled={step === 'submitting'}
            className="w-full py-4 bg-emerald-600 text-white rounded-xl font-bold text-lg hover:bg-emerald-700 transition-all hover:shadow-lg hover:shadow-emerald-600/25 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {step === 'submitting' ? (
              <><div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" /> Processing...</>
            ) : (
              <><Lock size={18} /> Place Order — ₹{grandTotal.toLocaleString('en-IN')}</>
            )}
          </button>
          <div className={`flex items-center justify-center gap-6 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            <span className="flex items-center gap-1"><Lock size={12} /> Encrypted</span>
            <span className="flex items-center gap-1"><Shield size={12} /> Secure</span>
            <span className="flex items-center gap-1"><Truck size={12} /> Fast Shipping</span>
          </div>
        </div>
      </div>

      {/* Delete address confirmation modal */}
      <AnimatePresence>
        {confirmDeleteId && (() => {
          const addr = savedAddresses.find(a => a.id === confirmDeleteId);
          return (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setConfirmDeleteId(null)} />
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 8 }}
                transition={{ duration: 0.15 }}
                className={`relative w-full max-w-sm rounded-2xl p-6 shadow-2xl ${isDark ? 'bg-gray-900 border border-gray-800' : 'bg-white'}`}
              >
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <Trash2 size={18} className="text-red-600" />
                  </div>
                  <div>
                    <h3 className={`font-bold text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>Delete Address</h3>
                    <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {addr ? (
                        <>Are you sure you want to delete <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{addr.address_line}</span>? This cannot be undone.</>
                      ) : 'Are you sure? This cannot be undone.'}
                    </p>
                  </div>
                </div>
                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteId(null)}
                    className={`px-4 py-2 text-sm font-semibold rounded-xl transition-colors ${isDark ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteAddress(confirmDeleteId)}
                    className="px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-xl hover:bg-red-700 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>
    </div>
  );
}
